import { AttachInternals, Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop } from '@stencil/core'

import Helpers from '../../utils/helpers'

/**
 * ## Design System
 *
 * Para a documentação completa de design, incluindo diretrizes de uso, acessibilidade e exemplos visuais, consulte o [Design System do GovBR](https://www.gov.br/ds/components/collapse?tab=designer).
 *
 * @slot default - Slot para o conteúdo expansível exibido ao abrir o collapse.
 * @slot trigger - Slot para personalizar o acionador (texto, ícones, imagens, etc.).
 */
@Component({
  tag: 'br-collapse',
  styleUrl: 'collapse.scss',
  shadow: true,
  formAssociated: true,
})
export class Collapse {
  /**
   * Referência ao elemento host do componente.
   * Utilize esta propriedade para acessar e manipular o elemento do DOM associado ao componente.
   */
  @Element() el: HTMLBrCollapseElement

  @AttachInternals() elementInternals: ElementInternals

  /**
   * Identificador único do componente. Um valor é gerado automaticamente quando não informado.
   */
  @Prop({ reflect: true }) readonly customId: string = Helpers.generateUniqueId('clp')

  /**
   * Controla se o collapse está aberto.
   */
  @Prop({ reflect: true, mutable: true }) open: boolean = false

  /**
   * Identifica o grupo de accordion; quando informado, mantém apenas um item aberto por vez no mesmo grupo.
   */
  @Prop() accordionGroup?: string = null

  /**
   * Classe CSS do ícone exibido quando o conteúdo está oculto.
   */
  @Prop() iconToShow: string = 'fa6-solid:chevron-down'

  /**
   * Classe CSS do ícone exibido quando o conteúdo está visível.
   */
  @Prop() iconToHide: string = 'fa6-solid:chevron-up'

  /**
   * Define a posição do ícone no acionador: 'left' ou 'right'.
   */
  @Prop() iconPosition: 'left' | 'right' = 'right'

  /**
   * Emitido ao expandir, com o identificador e o grupo de accordion.
   */
  @Event({ bubbles: true, composed: true }) brDidOpen: EventEmitter<{ id: string; accordionGroup: string }>

  /**
   * Emitido ao recolher, com o identificador e o grupo de accordion.
   */
  @Event({ bubbles: true, composed: true }) brDidClose: EventEmitter<{ id: string; accordionGroup: string }>

  /**
   * Fecha este collapse quando outro do mesmo grupo é aberto.
   */
  @Listen('brDidOpen', { target: 'document' })
  handleOpen(event: CustomEvent<{ id: string; accordionGroup: string }>) {
    if (
      this.accordionGroup &&
      this.accordionGroup === event.detail.accordionGroup &&
      event.detail.id !== this.customId
    ) {
      this.closeCollapse()
    }
  }

  /**
   * Ajusta o estado inicial do <details> para refletir o valor de open.
   */
  componentWillLoad() {
    this.syncDetailsWithOpen()
  }

  /**
   * Remove o listener do <details> ao desmontar o componente.
   */
  disconnectedCallback() {
    const details = this.el.shadowRoot?.querySelector('details')
    if (details !== null) {
      details.removeEventListener('toggle', this.handleCollapse)
    }
  }

  /**
   * Mantém o estado do <details> alinhado com a prop open.
   */
  private syncDetailsWithOpen() {
    const details = this.el.shadowRoot?.querySelector('details')
    if (details !== null) {
      details.open = this.open
    }
  }

  /**
   * Atualiza estado e emite eventos quando o <details> abre ou fecha.
   */
  private handleCollapse = (event: Event) => {
    const details = event.target as HTMLDetailsElement
    const isOpen = details.open

    this.open = isOpen
    if (this.open) {
      // Fecha outros do mesmo grupo de forma determinística antes de emitir evento
      this.closeOthersInGroup()
      this.brDidOpen.emit({ id: this.customId, accordionGroup: this.accordionGroup })
    } else {
      this.brDidClose.emit({ id: this.customId, accordionGroup: this.accordionGroup })
    }
  }

  /**
   * Abre o collapse programaticamente.
   */
  @Method()
  async openCollapse() {
    this.open = true
    this.syncDetailsWithOpen()
    // Fecha outros do mesmo grupo antes de emitir evento
    this.closeOthersInGroup()
    this.brDidOpen.emit({ id: this.customId, accordionGroup: this.accordionGroup })
  }

  /**
   * Fecha o collapse programaticamente.
   */
  @Method()
  async closeCollapse() {
    this.open = false
    this.syncDetailsWithOpen()
    this.brDidClose.emit({ id: this.customId, accordionGroup: this.accordionGroup })
  }

  /**
   * Fecha outros collapses abertos no mesmo accordion, buscando apenas no pai imediato.
   */
  private closeOthersInGroup() {
    if (this.accordionGroup == null || this.accordionGroup === '') return
    const parent = this.el.parentElement
    if (!parent) return
    const others = Array.from(parent.querySelectorAll('br-collapse')) as HTMLBrCollapseElement[]
    others
      .filter(
        (c) => c !== this.el && c.getAttribute('accordion-group') === this.accordionGroup && c.hasAttribute('open')
      )
      .forEach((c) => {
        // seta prop open=false diretamente para evitar múltiplas emissões desnecessárias
        c.open = false
        const details = c.shadowRoot?.querySelector('details')
        if (details) details.open = false
      })
  }

  /**
   * Seleciona o ícone conforme o estado atual.
   */
  private renderIcon() {
    return <br-icon icon-name={this.open ? this.iconToHide : this.iconToShow}></br-icon>
  }

  /**
   * Renderiza o <details> com acionador e conteúdo expansível.
   */
  render() {
    const itemCount = this.el.querySelectorAll('br-item').length

    return (
      <Host>
        <details class="br-collapse" open={this.open} onToggle={this.handleCollapse} id={this.customId}>
          <summary
            class="trigger"
            role="button"
            aria-expanded={String(this.open)}
            aria-controls={`content-${this.customId}`}
            aria-labelledby={`label-${this.customId}`}
            tabindex="0"
            aria-live="polite"
          >
            {this.iconPosition === 'left' && this.renderIcon()}
            <div class="trigger-content" id={`label-${this.customId}`}>
              <slot name="trigger"></slot>
            </div>
            {this.iconPosition === 'right' && this.renderIcon()}
          </summary>

          <div
            class="content"
            id={`content-${this.customId}`}
            aria-live="polite"
            aria-labelledby={`label-${this.customId}`}
          >
            {this.open && itemCount > 0 && (
              <div class="visually-hidden">
                {itemCount} {itemCount === 1 ? 'item' : 'itens'} na lista
              </div>
            )}
            <slot></slot>
          </div>
        </details>
      </Host>
    )
  }
}
